import { useState, useEffect, useRef } from 'react';

// Material-UI
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell as _TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { styled, CircularProgress, Typography, Box, Button } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';

const POS_STATUS_RANGES = {
    '2days': {
        label: '2 Days',
        days: 2,
    },
    '5days': {
        label: '5 Days',
        days: 5,
    },
};

export default function POSStatus() {
    const [range, setRange] = useState('2days');
    const [state, setState] = useState({
        data: null,
        loading: true,
        error: false,
        refresh: 0,
        lastLoad: moment(),
    });
    const [selectedError, setSelectedError] = useState(null);
    const [copiedField, setCopiedField] = useState(null);
    const copiedTimerRef = useRef(null);

    const { data, loading, error } = state;

    const copyToClipboard = async (field, value) => {
        if (!value) {
            return;
        }

        await navigator.clipboard.writeText(value);
        setCopiedField(field);

        window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = window.setTimeout(() => {
            setCopiedField(null);
        }, 1500);
    };

    useEffect(() => {
        const tz = moment.tz.guess();
        const start = moment()
            .subtract(POS_STATUS_RANGES[range].days, 'days')
            .format('YYYY-MM-DDTHH:mm:ss');
        const end = moment().format('YYYY-MM-DDTHH:mm:ss');

        fetchExpresso(`/admin/reports/pos_status?tz=${tz}&start=${start}&end=${end}`)
            .then(async (res) => {
                if (res.status !== 200) {
                    setState((s) => ({ ...s, loading: false, error: true }));
                    return;
                }

                const posErrorCodes = await res.json();

                let totalErrorCodes = 0;

                for (const errCode of posErrorCodes) {
                    totalErrorCodes += errCode.error_count;
                }

                for (const errCode of posErrorCodes) {
                    if (totalErrorCodes === 0) {
                        errCode.percent = 0;
                        continue;
                    }

                    errCode.percent = Number(
                        ((errCode.error_count / totalErrorCodes) * 100).toLocaleString(undefined, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        }),
                    );
                }

                setState((s) => ({ ...s, data: posErrorCodes, loading: false, error: false }));
            })
            .catch(() => {
                setState((s) => ({ ...s, loading: false, error: true }));
            });
    }, [range, state.refresh]);

    useEffect(() => {
        const interval = setInterval(
            () => {
                setState((s) => ({ ...s, refresh: s.refresh + 1, lastLoad: moment() }));
            },
            1000 * 60 * 5,
        );

        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        return () => {
            window.clearTimeout(copiedTimerRef.current);
        };
    }, []);

    if (loading) {
        return (
            <Root>
                <Status>
                    <CircularProgress disableShrink />
                </Status>
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Status>
                    <Typography>Unable to load stats</Typography>
                </Status>
            </Root>
        );
    }

    return (
        <Root>
            <Dialog open={selectedError !== null} onClose={() => setSelectedError(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Error Details</DialogTitle>
                <DialogContent dividers>
                    <Box mb={2}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="overline" color="text.secondary">
                                Code
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {copiedField === 'error_code' && (
                                    <Typography variant="caption" color="text.secondary">
                                        Text Copied!
                                    </Typography>
                                )}
                                <IconButton
                                    size="small"
                                    aria-label="Copy error code"
                                    onClick={() => copyToClipboard('error_code', selectedError?.error_code)}
                                >
                                    <ContentCopyOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        </Box>
                        <Typography variant="body1">{selectedError?.error_code || '-'}</Typography>
                    </Box>

                    <Box mb={2}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="overline" color="text.secondary">
                                Message
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {copiedField === 'error_message' && (
                                    <Typography variant="caption" color="text.secondary">
                                        Text Copied!
                                    </Typography>
                                )}
                                <IconButton
                                    size="small"
                                    aria-label="Copy error message"
                                    onClick={() => copyToClipboard('error_message', selectedError?.error_message)}
                                >
                                    <ContentCopyOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', minWidth: '100%' }}>
                                {selectedError?.error_message || '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="overline" color="text.secondary">
                                Data
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {copiedField === 'error_data' && (
                                    <Typography variant="caption" color="text.secondary">
                                        Text Copied!
                                    </Typography>
                                )}
                                <IconButton
                                    size="small"
                                    aria-label="Copy error data"
                                    onClick={() => copyToClipboard('error_data', selectedError?.error_data)}
                                >
                                    <ContentCopyOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', minWidth: '100%' }}>
                                {selectedError?.error_data || '-'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedError(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <CardHeader
                title="POS Error Codes"
                action={
                    <Box>
                        <Button
                            style={
                                range === '2days' ? { textDecoration: 'underline' } : undefined
                            }
                            onClick={() => setRange('2days')}
                        >
                            {POS_STATUS_RANGES['2days'].label}
                        </Button>
                        <Button
                            style={
                                range === '5days' ? { textDecoration: 'underline' } : undefined
                            }
                            onClick={() => setRange('5days')}
                        >
                            {POS_STATUS_RANGES['5days'].label}
                        </Button>
                    </Box>
                }
            />

            <CardContent>
                <TableContainer component={Box}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCellBold>CODE</TableCellBold>
                                <TableCellBold align="right">#</TableCellBold>
                                <TableCellBold align="right">%</TableCellBold>
                                <TableCellBold align="right">WHEN</TableCellBold>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data.map((e) => (
                                <TableRow
                                    key={`${e.error_code}-${e.error_message}`}
                                    hover
                                    onClick={() => setSelectedError(e)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <ErrorCodeCell>
                                        <ErrorCodeText>{e.error_code}</ErrorCodeText>
                                        <ErrorCodeText variant='caption'>{e.error_message || '-'}</ErrorCodeText>
                                    </ErrorCodeCell>
                                    <TableCell width={30} align="right">{e.error_count}</TableCell>
                                    <TableCell width={30} align="right">{e.percent}%</TableCell>
                                    <TableCell width={30} align="right">
                                        {getRelativeTime(e.last_error, 'short')}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell align="center" colSpan={5}>
                                        <Typography variant="subtitle2">No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box mt={2}>
                    <Typography variant="caption">
                        updated {state.lastLoad.format('h:mmA')}
                    </Typography>
                </Box>
            </CardContent>
        </Root>
    );
}

const Root = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const Status = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
});

const TableCellBold = styled(_TableCell)(({ theme }) => ({
    fontWeight: 'bold',
}));

const TableCell = styled(_TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
}));

const ErrorCodeCell = styled(TableCell)(({ theme }) => ({
    maxWidth: 200,
    overflow: 'hidden',
    [theme.breakpoints.down('xl')]: {
        maxWidth: 300,
    },
}));

const ErrorCodeText = styled(Typography)({
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});
